import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './ViewThruOtpModal.module.scss'

const CODE_LENGTH = 6

export type ViewThruOtpModalProps = {
    onSubmit: (otpCode: string) => Promise<void>
    onCancel: () => void
    errorMessage?: string | null
    isSubmitting?: boolean
}

/** Synced from sentinel-web/packages/form-ui/src/components/ViewThruOtpModal */
export const ViewThruOtpModal = ({
    onSubmit,
    onCancel,
    errorMessage,
    isSubmitting = false,
}: ViewThruOtpModalProps) => {
    const [otpCode, setOtpCode] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)
    // The code we last handed to onSubmit, so auto-submit fires once per code
    // rather than on every re-render while the field stays full.
    const submittedCodeRef = useRef<string | null>(null)

    const submitCode = useCallback(
        async (code: string) => {
            if (code.length !== CODE_LENGTH || isSubmitting) return
            if (submittedCodeRef.current === code) return
            submittedCodeRef.current = code
            await onSubmit(code)
        },
        [isSubmitting, onSubmit]
    )

    // Submit as soon as the sixth digit lands, including SMS autofill which
    // populates all six at once.
    useEffect(() => {
        if (otpCode.length < CODE_LENGTH) {
            // Let an edited code through again, even if it matches a failed attempt.
            submittedCodeRef.current = null
            return
        }
        void submitCode(otpCode)
    }, [otpCode, submitCode])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        await submitCode(otpCode)
    }

    return createPortal(
        <div
            className={styles.overlay}
            role="dialog"
            aria-modal="true"
            aria-labelledby="viewthru-otp-title"
        >
            <div className={styles.modal}>
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onCancel}
                    disabled={isSubmitting}
                    aria-label="Close verification"
                >
                    ×
                </button>
                <h2 id="viewthru-otp-title" className={styles.title}>
                    Check your phone
                </h2>
                <p className={styles.description}>
                    We’ve sent a six-digit code to your mobile. Enter it below to continue signing your
                    documents.
                </p>
                <form onSubmit={handleSubmit}>
                    <label className={styles.srOnly} htmlFor="viewthru-otp-input">
                        Six-digit verification code
                    </label>
                    <div
                        className={`${styles.codeFields} ${errorMessage ? styles.codeFieldsError : ''}`}
                        onClick={() => inputRef.current?.focus()}
                    >
                        {Array.from({ length: CODE_LENGTH }, (_, index) => (
                            <span
                                key={index}
                                className={`${styles.codeField} ${
                                    index === otpCode.length && !isSubmitting ? styles.codeFieldActive : ''
                                }`}
                                aria-hidden="true"
                            >
                                {otpCode[index] ?? ''}
                            </span>
                        ))}
                    </div>
                    <input
                        ref={inputRef}
                        id="viewthru-otp-input"
                        className={styles.codeInput}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="[0-9]*"
                        maxLength={CODE_LENGTH}
                        value={otpCode}
                        onChange={(e) =>
                            setOtpCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))
                        }
                        disabled={isSubmitting}
                        autoFocus
                    />
                    {errorMessage ? (
                        <p className={styles.error} role="alert">
                            {errorMessage}
                        </p>
                    ) : (
                        <p className={styles.hint}>The code may take up to a minute to arrive.</p>
                    )}
                    <div className={styles.actions}>
                        <button
                            className={styles.primaryButton}
                            type="submit"
                            disabled={isSubmitting || otpCode.length !== CODE_LENGTH}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className={styles.spinner} aria-hidden="true" />
                                    Verifying
                                </>
                            ) : (
                                'Verify and continue'
                            )}
                        </button>
                        <button
                            className={styles.cancelButton}
                            type="button"
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    )
}
